import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { fetchLiftangoInsights, LiftangoInsightsArticle } from '../../../../services/api/liftangoResourcesService';
import { liftangoBrandTokens } from '../../../../theme';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
const SIDEBAR = {
  label: liftangoBrandTokens.simulationResults.sidebarMutedText,
  border: liftangoBrandTokens.simulationResults.sidebarBorder,
} as const;

const CARD = {
  shell: 'linear-gradient(165deg, rgba(248,250,252,0.97), rgba(226,232,240,0.94))',
  shellMuted: 'rgba(244,247,251,0.82)',
  stage: 'linear-gradient(175deg, rgba(15,23,42,0.14), rgba(30,41,59,0.06))',
  mainSurface: '#ffffff',
  sideSurface: 'rgba(235,241,248,0.9)',
  title: '#0f172a',
  body: '#334155',
  controls: '#475569',
  controlsHover: '#0f172a',
} as const;

const FONT_FAMILY = '"Roboto", sans-serif';
const AUTOPLAY_INTERVAL_MS = 6000;
const FIXED_CAROUSEL_HEIGHT_PX = 336;
const LIFTANGO_LINKEDIN_URL = 'https://www.linkedin.com/company/liftango/posts/?feedView=all';

function openExternal(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface LiftangoInsightsCarouselProps {
  stretchToContainer?: boolean;
  enabled?: boolean;
}

interface SidePreviewCardProps {
  article: LiftangoInsightsArticle;
  direction: 'left' | 'right';
  onClick: () => void;
}

const SidePreviewCard: React.FC<SidePreviewCardProps> = ({ article, direction, onClick }) => {
  const { t } = useTranslation('simulation');
  const isLeft = direction === 'left';

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={t('results.insights.previewArticleAria', {
        direction: isLeft
          ? t('results.insights.previousLabel')
          : t('results.insights.nextLabel'),
        title: article.title,
      })}
      sx={{
        all: 'unset',
        position: 'absolute',
        top: 30,
        bottom: 32,
        ...(isLeft ? { left: -62 } : { right: -62 }),
        width: '47%',
        maxWidth: 210,
        borderRadius: 2,
        overflow: 'hidden',
        zIndex: 1,
        cursor: 'pointer',
        backgroundColor: CARD.sideSurface,
        transform: isLeft
          ? 'rotateY(44deg) translateZ(-84px) scale(0.92)'
          : 'rotateY(-44deg) translateZ(-84px) scale(0.92)',
        transformOrigin: isLeft ? 'left center' : 'right center',
        boxShadow: isLeft
          ? '-12px 16px 36px rgba(15,23,42,0.28)'
          : '12px 16px 36px rgba(15,23,42,0.28)',
        border: '1px solid rgba(15,23,42,0.15)',
        transition: 'transform 160ms ease, filter 160ms ease',
        filter: 'saturate(0.85) brightness(0.84)',
        '&:hover': {
          transform: isLeft
            ? 'rotateY(36deg) translateZ(-72px) scale(0.95)'
            : 'rotateY(-36deg) translateZ(-72px) scale(0.95)',
          filter: 'saturate(0.94) brightness(0.92)',
        },
      }}
    >
      <Box
        component="img"
        src={article.imageUrl}
        alt={article.title}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center 38%',
          display: 'block',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isLeft
            ? 'linear-gradient(90deg, rgba(15,23,42,0.72), rgba(15,23,42,0.14) 58%, rgba(0,0,0,0.03))'
            : 'linear-gradient(270deg, rgba(15,23,42,0.72), rgba(15,23,42,0.14) 58%, rgba(0,0,0,0.03))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          ...(isLeft ? { left: 12, right: 10 } : { left: 10, right: 12 }),
          bottom: 10,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            color: '#171a1f',
            fontSize: '0.76rem',
            fontWeight: 700,
            lineHeight: 1.35,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textShadow: '0 2px 8px rgba(0,0,0,0.45)',
            textAlign: isLeft ? 'left' : 'right',
          }}
        >
          {article.title}
        </Typography>
      </Box>
    </Box>
  );
};

const LiftangoInsightsCarousel: React.FC<LiftangoInsightsCarouselProps> = ({ stretchToContainer = false, enabled = true }) => {
  const { t } = useTranslation('simulation');
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState<LiftangoInsightsArticle[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [imageErrored, setImageErrored] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const response = await fetchLiftangoInsights(6);
        if (!active) return;
        setArticles(response.articles);
      } catch {
        if (!active) return;
        setArticles([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (paused || articles.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % articles.length);
    }, AUTOPLAY_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [articles.length, paused]);

  useEffect(() => {
    if (index >= articles.length) {
      setIndex(0);
    }
  }, [articles.length, index]);

  const activeArticle = useMemo(() => {
    if (articles.length === 0) return null;
    return articles[index] ?? articles[0];
  }, [articles, index]);

  const prevArticle = useMemo(() => {
    if (articles.length <= 1) return null;
    const prevIndex = (index - 1 + articles.length) % articles.length;
    return articles[prevIndex] ?? null;
  }, [articles, index]);

  const nextArticle = useMemo(() => {
    if (articles.length <= 1) return null;
    const nextIndex = (index + 1) % articles.length;
    return articles[nextIndex] ?? null;
  }, [articles, index]);

  useEffect(() => {
    setImageErrored(false);
  }, [activeArticle?.id]);

  const goPrev = () => {
    if (articles.length <= 1) return;
    setIndex((prev) => (prev - 1 + articles.length) % articles.length);
  };

  const goNext = () => {
    if (articles.length <= 1) return;
    setIndex((prev) => (prev + 1) % articles.length);
  };

  const fixedHeight = stretchToContainer ? '100%' : `${FIXED_CAROUSEL_HEIGHT_PX}px`;

  if (loading) {
    return (
      <Box
        sx={{
          border: `1px solid ${SIDEBAR.border}`,
          borderRadius: 2.5,
          overflow: 'hidden',
          background: CARD.shell,
          boxShadow: '0 20px 40px -24px rgba(15, 23, 42, 0.55), inset 0 1px 0 #52565e',
          height: fixedHeight,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ p: 1.25, flex: 1 }}>
          <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
        </Box>
        <Box sx={{ px: 1, pb: 0.6 }}>
          <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: 999 }} />
        </Box>
      </Box>
    );
  }

  if (!activeArticle) {
    return (
      <Box
        sx={{
          border: `1px solid ${SIDEBAR.border}`,
          borderRadius: 2.5,
          overflow: 'hidden',
          background: CARD.shell,
          boxShadow: '0 20px 40px -24px rgba(15, 23, 42, 0.55), inset 0 1px 0 #52565e',
          height: fixedHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: SIDEBAR.label, fontFamily: FONT_FAMILY, textAlign: 'center' }}>
          {t('results.insights.noArticles')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      sx={{
        border: `1px solid ${SIDEBAR.border}`,
        borderRadius: 2.5,
        overflow: 'hidden',
        background: CARD.shell,
        boxShadow: '0 20px 40px -24px rgba(15, 23, 42, 0.55), inset 0 1px 0 #52565e',
        height: fixedHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 1.2,
          pt: 1.2,
          pb: 0.8,
          position: 'relative',
          perspective: 1600,
          minHeight: 260,
          flex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '6px 10px 10px',
            borderRadius: 2,
            pointerEvents: 'none',
            background: CARD.stage,
          },
        }}
      >
        {prevArticle ? <SidePreviewCard article={prevArticle} direction="left" onClick={goPrev} /> : null}
        {nextArticle ? <SidePreviewCard article={nextArticle} direction="right" onClick={goNext} /> : null}

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            borderRadius: 2.25,
            border: '1px solid rgba(15,23,42,0.14)',
            overflow: 'hidden',
            backgroundColor: CARD.mainSurface,
            boxShadow: '0 18px 40px -28px rgba(15, 23, 42, 0.7)',
            mx: 'auto',
            width: 'calc(100% - 78px)',
            minHeight: 228,
            transform: 'translateZ(0)',
          }}
        >
          {imageErrored ? (
            <Box
              sx={{
                width: '100%',
                height: 150,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                textAlign: 'center',
                color: '#64748b',
                fontFamily: FONT_FAMILY,
                fontSize: '0.8rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, rgba(226,232,240,0.8), rgba(203,213,225,0.7))',
              }}
            >
              {t('results.insights.imagePlaceholder')}
            </Box>
          ) : (
            <Box
              component="img"
              src={activeArticle.imageUrl}
              alt={activeArticle.title}
              onError={() => setImageErrored(true)}
              sx={{
                width: '100%',
                height: 150,
                objectFit: 'cover',
                objectPosition: 'center 35%',
                display: 'block',
              }}
            />
          )}

          <Box
            sx={{
              p: 1.2,
              pt: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              minHeight: 78,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: CARD.title,
                  fontFamily: FONT_FAMILY,
                  fontWeight: 800,
                  fontSize: '1rem',
                  lineHeight: 1.35,
                  mb: 0.65,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  minHeight: '2.7em',
                }}
              >
                {activeArticle.title}
              </Typography>

              <Typography
                sx={{
                  color: CARD.body,
                  fontFamily: FONT_FAMILY,
                  fontSize: '0.86rem',
                  lineHeight: 1.52,
                  mb: 1,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: stretchToContainer ? 6 : 4,
                  WebkitBoxOrient: 'vertical',
                  minHeight: '3.04em',
                }}
              >
                {activeArticle.shortDescription}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => openExternal(activeArticle.link)}
              sx={{
                textTransform: 'none',
                px: 1.6,
                py: 0.35,
                minWidth: 102,
                borderRadius: 999,
                borderWidth: 1.5,
                fontFamily: FONT_FAMILY,
                color: liftangoBrandTokens.palette.primary,
                borderColor: 'rgba(238, 115, 52, 0.64)',
                fontWeight: 700,
                fontSize: '0.85rem',
                backgroundColor: '#374151',
                '&:hover': {
                  borderColor: liftangoBrandTokens.palette.primary,
                  backgroundColor: '#fff',
                },
              }}
              aria-label={t('results.insights.readMoreAria', { title: activeArticle.title })}
            >
              {t('results.insights.readMore')}
            </Button>
          </Box>
        </Box>
      </Box>

      {articles.length > 1 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 0.9,
            py: 0.55,
            borderTop: `1px solid ${SIDEBAR.border}`,
            backgroundColor: CARD.shellMuted,
          }}
        >
          <Box sx={{ width: 120 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55 }}>
            <IconButton
              size="small"
              onClick={goPrev}
              aria-label={t('results.insights.previousArticle')}
              sx={{
                color: CARD.controls,
                '&:hover': { color: CARD.controlsHover, backgroundColor: 'rgba(15,23,42,0.06)' },
              }}
            >
              <Icon name="chevron-left" size={20} />
            </IconButton>

            {articles.map((article, dotIndex) => (
              <Box
                key={article.id}
                component="button"
                type="button"
                aria-label={t('results.insights.goToArticle', { index: dotIndex + 1 })}
                onClick={() => setIndex(dotIndex)}
                sx={{
                  p: 0,
                  border: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  backgroundColor: dotIndex === index ? liftangoBrandTokens.palette.primary : '#cbd5e1',
                  boxShadow: dotIndex === index
                    ? '0 0 0 1px #171a1f, 0 0 8px rgba(238,115,52,0.35)'
                    : 'inset 0 0 0 1px rgba(71,85,105,0.25)',
                }}
              />
            ))}

            <IconButton
              size="small"
              onClick={goNext}
              aria-label={t('results.insights.nextArticle')}
              sx={{
                color: CARD.controls,
                '&:hover': { color: CARD.controlsHover, backgroundColor: 'rgba(15,23,42,0.06)' },
              }}
            >
              <Icon name="chevron-right" size={20} />
            </IconButton>
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon size={20} d={ICON_FALLBACKS.LinkedIn} />}
            onClick={() => openExternal(LIFTANGO_LINKEDIN_URL)}
            sx={{
              minWidth: 120,
              textTransform: 'none',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: '0.78rem',
              borderColor: 'rgba(59, 130, 246, 0.45)',
              color: '#1d4ed8',
              backgroundColor: '#52565e',
              '&:hover': {
                borderColor: '#2563eb',
                backgroundColor: '#171a1f',
              },
            }}
            aria-label={t('results.insights.openLinkedIn')}
          >
            {t('results.insights.linkedIn')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default LiftangoInsightsCarousel;
